<template>
  <ToolPageLayout
    title="财务总监智擎工作台-创智物联"
    subtitle="月度经营报告 · 7+3滚动预测 · What-if模拟 · 数据刷新: 2026-06-16 08:42"
    period="报告周期：2026年5月（月结完成）"
    tag="月结已锁定 · SAP CO-FI核对一致"
  >
    <div v-if="loading" class="sc loading-state">数据加载中...</div>

    <div v-else class="sc forecast-card">
      <div class="sc-header">
        <div class="sc-title">
          📈 7+3滚动预测 · 最小可行P&L
          <span class="tag tag-purple">展望Q3</span>
          <span class="tag tag-blue">含材差</span>
        </div>
        <div class="header-meta">
          <span class="meta-text">更新频率: {{ meta.update_freq }} | 数据截止: {{ meta.data_cutoff }}</span>
          <span class="sc-badge tag-amber">在手订单覆盖度 {{ meta.order_coverage }}%</span>
          <span class="sc-badge tag-blue">预测置信度评估</span>
        </div>
      </div>

      <div class="forecast-pnl">
        <table class="forecast-pnl-table">
          <thead>
            <tr>
              <th v-for="(col, ci) in columns" :key="ci" :class="colClass(ci)">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, i) in rows">
              <tr v-if="row.type === 'section'" :key="'s-' + i" class="row-section">
                <td :colspan="columns.length">▶ {{ row.title }}</td>
              </tr>
              <tr v-else-if="row.type === 'assumption'" :key="'a-' + i" class="assumption-row">
                <td :colspan="columns.length" v-html="formatAssumption(row.text)" />
              </tr>
              <tr
                v-else-if="row.type === 'data'"
                :key="'d-' + i"
                :class="[row.row_class === 'material' ? 'row-material' : '', row.row_class === 'bottom' ? 'row-bottom' : '']"
              >
                <td :class="nameCellClass(row)">{{ formatAccountName(row) }}</td>
                <td :class="cellClass(row, 'may_actual')">{{ formatVal(row, 'may_actual') }}</td>
                <td :class="cellClass(row, 'q1_actual')">{{ formatVal(row, 'q1_actual') }}</td>
                <td :class="['col-divider', cellClass(row, 'jun_fcst')]">{{ formatVal(row, 'jun_fcst') }}</td>
                <td :class="cellClass(row, 'q2_fcst')">{{ formatVal(row, 'q2_fcst') }}</td>
                <td :class="cellClass(row, 'q3_fcst')">{{ formatVal(row, 'q3_fcst') }}</td>
                <td :class="cellClass(row, 'fy_fcst')">{{ formatVal(row, 'fy_fcst') }}</td>
                <td class="col-conf">
                  <span :class="confClass(row.confidence)">{{ confLabel(row.confidence) }}</span>
                </td>
              </tr>
            </template>

            <tr v-if="summaryCards.length">
              <td :colspan="columns.length" class="summary-row">
                <div class="summary-cards">
                  <div
                    v-for="(card, ci) in summaryCards"
                    :key="ci"
                    :class="['summary-card', 'tone-' + card.tone]"
                  >
                    <div class="summary-card-title">{{ card.title }}</div>
                    <div :class="['summary-card-val', { small: card.value_small }]">{{ card.value }}</div>
                    <div class="summary-card-sub">{{ card.sub }}</div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="footnote" class="annot-footnote">
        <b>ⓘ 预测方法论</b>：{{ footnoteMethodology }}
      </div>
    </div>
  </ToolPageLayout>
</template>

<script>
import ToolPageLayout from './ToolPageLayout.vue'
import { fetchRollingForecast, unwrapFpaResponse } from '@/api/fpa'
import { MOCK_ROLLING_PNL, MOCK_ROLLING_FORECAST } from './mockData'

const DEFAULT_COLUMNS = [
  '科目（万元）', '5月实际', 'Q1实际', '6月预测', 'Q2预测', 'Q3预测', '全年预测', '置信度'
]

const PERIOD_COL_MAP = {
  202606: 'jun_fcst',
  202607: 'q2_fcst',
  202608: 'q3_fcst',
  202612: 'fy_fcst'
}

export default {
  name: 'ToolFpaGmForecast',
  components: { ToolPageLayout },
  data() {
    return {
      loading: true,
      columns: DEFAULT_COLUMNS,
      meta: {},
      rows: [],
      summaryCards: [],
      footnote: ''
    }
  },
  computed: {
    footnoteMethodology() {
      if (!this.footnote) return ''
      return this.footnote.replace(/^预测方法论[：:]\s*/, '')
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const raw = unwrapFpaResponse(await fetchRollingForecast())
        this.applyPnl(this.normalizeRollingForecast(raw))
      } catch (e) {
        this.applyPnl(this.normalizeRollingForecast(null))
      } finally {
        this.loading = false
      }
    },
    applyPnl(pnl) {
      this.columns = pnl.columns || DEFAULT_COLUMNS
      this.meta = pnl.meta || MOCK_ROLLING_PNL.meta
      this.rows = pnl.rows || []
      this.summaryCards = pnl.summary_cards || []
      this.footnote = pnl.footnote || ''
      if (!this.rows.length) {
        const fallback = this.normalizeRollingForecast(null)
        this.rows = fallback.rows
        this.summaryCards = fallback.summary_cards
        this.footnote = fallback.footnote
      }
    },
    normalizeRollingForecast(raw) {
      if (raw && raw.sections && raw.sections.length) {
        return this.parseApiSections(raw)
      }
      if (raw && raw.rows && raw.rows.length && !raw.sections) {
        return {
          columns: raw.columns || DEFAULT_COLUMNS,
          meta: raw.meta || MOCK_ROLLING_PNL.meta,
          rows: raw.rows,
          summary_cards: raw.summary_cards || MOCK_ROLLING_PNL.summary_cards,
          footnote: raw.footnote || MOCK_ROLLING_PNL.footnote
        }
      }

      const base = JSON.parse(JSON.stringify(MOCK_ROLLING_PNL))
      const list = Array.isArray(raw) && raw.length ? raw : MOCK_ROLLING_FORECAST

      list.forEach(item => {
        if (!item.account_code) return
        const col = PERIOD_COL_MAP[item.period_key]
        if (!col) return
        const row = base.rows.find(r => r.type === 'data' && r.account_code === item.account_code)
        if (!row) return
        row[col] = item.forecast_wan
        if (item.confidence_level != null) row.confidence = item.confidence_level
      })

      return {
        columns: DEFAULT_COLUMNS,
        meta: base.meta,
        rows: base.rows,
        summary_cards: base.summary_cards,
        footnote: base.footnote
      }
    },
    parseApiSections(data) {
      const rows = []

      data.sections.forEach(section => {
        rows.push({ type: 'section', title: section.group })

        ;(section.rows || []).forEach(r => {
          rows.push({
            type: 'data',
            account_code: r.account_code,
            account_name: r.account_name,
            indent: r.indent > 0,
            indent_level: r.indent || 0,
            small: (r.indent || 0) >= 2,
            is_pct: r.is_rate,
            is_sub: r.is_sub,
            row_class: this.resolveRowClass(r),
            may_actual: r.m5_actual,
            q1_actual: r.q1_actual,
            jun_fcst: r.m6_forecast,
            q2_fcst: r.q2_forecast,
            q3_fcst: r.q3_forecast,
            fy_fcst: r.fy_forecast,
            confidence: r.confidence_pct
          })
        })

        if (section.assumption) {
          rows.push({ type: 'assumption', text: section.assumption })
        }
        if (section.mat_var_assumption) {
          rows.push({ type: 'assumption', text: `材差假设: ${section.mat_var_assumption}` })
        }
      })

      return {
        columns: data.columns || DEFAULT_COLUMNS,
        meta: {
          update_freq: data.update_freq || '每周五',
          data_cutoff: this.formatCutoff(data.data_cutoff),
          order_coverage: data.order_coverage_pct
        },
        rows,
        summary_cards: [],
        footnote: data.confidence_note
          ? `预测方法论：${data.confidence_note}`
          : ''
      }
    },
    resolveRowClass(r) {
      const code = r.account_code || ''
      if (['GP', 'GP_RATE', 'OP', 'NP', 'OPEX'].includes(code)) return 'bottom'
      if (code.startsWith('COST_MAT') || (r.is_sub && !code.startsWith('OPEX'))) return 'material'
      return ''
    },
    formatCutoff(value) {
      if (!value) return ''
      const m = String(value).match(/(\d{4})-(\d{2})-(\d{2})/)
      if (m) return `${parseInt(m[2], 10)}月${parseInt(m[3], 10)}日`
      return value
    },
    formatAccountName(row) {
      if (row.account_code === 'COST_MAT_VAR') return `⚠ ${row.account_name}`
      if (row.indent_level >= 2) return `└ ${row.account_name}`
      if (row.indent_level === 1 && row.is_sub) return row.account_name
      return row.account_name
    },
    nameCellClass(row) {
      return {
        indent: row.indent_level === 1,
        indent2: row.indent_level >= 2,
        small: row.small
      }
    },
    colClass(index) {
      if (index === 0) return ''
      if (index <= 2) return 'col-hist'
      if (index === 3) return 'col-divider col-fcst'
      if (index === 7) return 'col-conf'
      return 'col-fcst'
    },
    formatVal(row, col) {
      const val = row[col]
      if (val == null || val === '') return '—'
      const n = Number(val)
      if (row.is_pct) return n.toFixed(1) + '%'
      if (row.small || row.indent_level >= 2) {
        return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
      }
      return Math.round(n).toLocaleString('zh-CN')
    },
    cellClass(row, col) {
      const classes = []
      const val = row[col]
      const isFcst = ['jun_fcst', 'q2_fcst', 'q3_fcst', 'fy_fcst'].includes(col)
      const code = row.account_code

      if (['REV', 'COST', 'GP', 'GP_RATE', 'GM', 'OP', 'NP', 'OPEX'].includes(code) && isFcst) {
        classes.push('bold')
      }

      if (code === 'GP' && col === 'q3_fcst' && val > 0) classes.push('tone-green')
      else if (code === 'GP_RATE' && col === 'q3_fcst') classes.push('tone-green')
      else if (isFcst && row.row_class !== 'material') classes.push('tone-purple')

      if (!row.is_pct && val < 0) classes.push('tone-red')
      if (row.row_class === 'material' && isFcst && val < 0 && !classes.includes('tone-red')) {
        classes.push('tone-amber')
      }

      return classes
    },
    formatAssumption(text) {
      if (!text) return ''
      if (text.startsWith('材差假设')) {
        return `↳ <b>材差假设</b>: ${text.replace(/^材差假设[：:]\s*/, '')}`
      }
      return `↳ <b>假设</b>: ${text}`
    },
    confLabel(level) {
      if (level == null || level === '') return '—'
      const pct = level <= 1 ? Math.round(level * 100) : Math.round(level)
      return pct + '%'
    },
    confClass(level) {
      if (level == null || level === '') return 'confidence'
      const pct = level <= 1 ? Math.round(level * 100) : Math.round(level)
      if (pct >= 80) return 'confidence c80'
      if (pct >= 50) return 'confidence c50'
      return 'confidence'
    }
  }
}
</script>

<style lang="scss" scoped>
@import './tool-page.scss';

.forecast-card {
  .header-meta {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .meta-text {
    font-size: 10px;
    color: var(--muted);
  }
}

.forecast-pnl { overflow-x: auto; }

.forecast-pnl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  min-width: 860px;

  thead th {
    padding: 6px 8px;
    text-align: right;
    font-size: 9px;
    color: var(--muted);
    font-weight: 500;
    border-bottom: 2px solid var(--line);
    white-space: nowrap;
    &:first-child { text-align: left; }
    &.col-hist { background: rgba(45, 127, 249, 0.04); color: var(--blue); }
    &.col-fcst { background: rgba(127, 85, 217, 0.04); color: var(--purple); font-weight: 700; }
    &.col-divider { border-left: 3px solid var(--purple); }
    &.col-conf { text-align: center; width: 60px; }
  }

  tbody td {
    padding: 6px 8px;
    text-align: right;
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
    &:first-child { text-align: left; font-weight: 500; }
    &.col-divider { border-left: 3px solid rgba(127, 85, 217, 0.2); }
    &.col-conf { text-align: center; }
    &.indent { padding-left: 20px; font-weight: 400; }
    &.indent2 { padding-left: 32px; font-weight: 400; font-size: 10px; }
    &.small { font-size: 10px; }
    &.bold { font-weight: 700; }
    &.tone-red { color: var(--red); font-weight: 600; }
    &.tone-green { color: var(--green); font-weight: 700; }
    &.tone-blue { color: var(--blue); font-weight: 700; }
    &.tone-amber { color: var(--amber); font-weight: 600; }
    &.tone-purple { color: var(--purple); }
  }

  .row-section td {
    font-weight: 700;
    background: var(--surface2);
    color: var(--blue2);
    font-size: 11px;
  }
  .row-material td:first-child { color: var(--amber); font-weight: 600; }
  .row-material { background: rgba(217, 130, 19, 0.03); }
  .row-bottom td { font-weight: 700; font-size: 11px; }

  .assumption-row td {
    font-size: 9px;
    color: var(--muted);
    padding: 4px 8px;
    background: rgba(127, 85, 217, 0.03);
    border-bottom: 1px dashed var(--border);
    text-align: left;
    b { color: var(--text2); }
  }

  .confidence {
    font-size: 8px;
    color: var(--muted);
    font-weight: 400;
    &.c80 { color: var(--green); }
    &.c50 { color: var(--amber); }
  }
}

.summary-row {
  padding: 10px 8px !important;
}
.summary-cards {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.summary-card {
  flex: 1;
  min-width: 180px;
  padding: 6px 10px;
  border-radius: 6px;
  &.tone-green {
    background: rgba(10, 166, 106, 0.05);
    border: 1px solid rgba(10, 166, 106, 0.15);
    .summary-card-title { color: var(--green); }
  }
  &.tone-amber {
    background: rgba(217, 130, 19, 0.05);
    border: 1px solid rgba(217, 130, 19, 0.15);
    .summary-card-title { color: var(--amber); }
  }
  &.tone-red {
    background: rgba(229, 75, 61, 0.05);
    border: 1px solid rgba(229, 75, 61, 0.15);
    .summary-card-title { color: var(--red); }
  }
}
.summary-card-title {
  font-size: 10px;
  font-weight: 700;
}
.summary-card-val {
  font-size: 12px;
  font-weight: 700;
  margin: 2px 0;
  &.small { font-size: 11px; color: var(--red); }
}
.summary-card-sub {
  font-size: 8px;
  color: var(--muted);
  line-height: 1.4;
}

.annot-footnote {
  margin-top: 12px;
  padding: 10px 14px;
  background: var(--surface2);
  border-radius: var(--radius-sm);
  font-size: 9px;
  color: var(--muted);
  line-height: 1.6;
  b { color: var(--text2); }
}

.loading-state {
  padding: 40px;
  text-align: center;
  font-size: 12px;
  color: var(--muted);
}
</style>
